import React from 'react';

import {
  Add as AddIcon,
  BorderColor as BorderColorIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { Button } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import {
  EmployerStaffInfoFragment,
  UserStatus,
  useResendInviteToEmployerStaffMutation,
} from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import {
  Table,
  TableColumn,
  TableFilter,
  TableRowAction,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { BusinessRole } from 'src/constants/user';
import { useNotification } from 'src/hooks/app';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { hasOneOfRoles } from 'src/utils/role';

export interface EmployerStaffTableEMPAdminProps {
  employerId?: Nullable<string>;
  employerStaffList?: EmployerStaffInfoFragment[];
  employerStaffCount?: number;
  pagination: PaginationContextValue;
  onRefresh?: () => Promise<void>;
  loading: boolean;
}

export const EmployerStaffTableEMPAdmin: React.FC<EmployerStaffTableEMPAdminProps> = ({
  employerStaffList = [],
  employerStaffCount = 0,
  employerId,
  onRefresh,
  pagination,
  loading,
}) => {
  const { createApolloContext } = useNotification();

  const { openModal } = useModal();
  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  const [resendInviteToEmployerStaff] = useResendInviteToEmployerStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(() => {
    const actions: Array<TableRowAction<EmployerStaffInfoFragment>> = [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: staff =>
          openModal(ModalId.EmployerStaffDetailsModal, {
            employerStaffId: staff.id,
            employerId,
          }),
      },
      {
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'company',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToEmployerStaff({
                variables: { employerStaffId: staff.id },
              });
              return data?.resendInviteToEmployerStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      },
    ];
    return actions;
  }, [openModal, resendInviteToEmployerStaff, employerId]);

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => openModal(ModalId.EmployerAdminInviteStaffModal),
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [openModal],
  );

  return (
    <Table
      dataSource={employerStaffList}
      rowCount={employerStaffCount}
      columns={COLUMNS}
      pagination={pagination}
      onSearch={setSearchValue}
      loading={loading}
      rowActions={rowActions}
      topActions={topActions}
      filters={filters}
      tableTheme={TableThemes.withoutProfileIcon}
      onRefresh={onRefresh}
      onSort={setSortOption}
    />
  );
};

const COLUMNS: TableColumn<EmployerStaffInfoFragment>[] = [
  {
    title: 'Full Name',
    key: 'user.fullName',
    sortable: true,
    render: (_valueByDataPath, employerStaff, { rowActions }) => {
      const isEmployerAdmin = hasOneOfRoles(employerStaff, [BusinessRole.EmployerAdmin]);
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(employerStaff)}
          label={employerStaff.user.fullName}
          labelPostfix={isEmployerAdmin ? 'Admin' : undefined}
          logoUrl={employerStaff.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'phoneNumber',
    render: (_valueByDataPath, staff) =>
      parseNullableValue(formatPhoneNumber(staff.user.phoneNumber)),
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, employerStaff) => parseNullableValue(employerStaff.user.email),
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, employerStaff) => (
      <UserStatusChip userStatus={employerStaff.user.status} />
    ),
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
